import { useState, useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm, Controller, useFieldArray } from 'react-hook-form'
import {
  CheckCircleIcon,
  XCircleIcon,
  Stars01Icon,
  AlertCircleIcon,
  MinusCircleIcon,
  PlusIcon,
  Trash01Icon,
} from '@untitledui-icons/react/line'
import { Button } from '@/components/base/buttons'
import { TextArea } from '@/components/base/textarea/textarea'
import { toast } from '@/lib/toast'
import { cn } from '@/lib/utils'
import { getErrorMessage } from '@/lib/api'
import { useUpdateConversation, useAutoQAConversation, useViolationCodes } from '@/hooks/api/useConversations'
import { useAuthStore } from '@/stores/authStore'
import type {
  Conversation,
  QALabel,
  BusinessOutcome,
  TrainingSuitability,
  ViolationSeverity,
} from '@/types/conversation'

interface ConversationQALabelProps {
  conversation: Conversation
  className?: string
}

interface ViolationFormData {
  code: string
  severity: ViolationSeverity
  description: string
}

interface QALabelFormData {
  business_outcome: BusinessOutcome | ''
  outcome_notes: string
  training_suitability: TrainingSuitability | ''
  adherence_score: number
  violations: ViolationFormData[]
}

// Business Outcome config
const BUSINESS_OUTCOME_CONFIG: {
  value: BusinessOutcome
  icon: typeof CheckCircleIcon
  color: string
  bgColor: string
}[] = [
  {
    value: 'success',
    icon: CheckCircleIcon,
    color: 'text-green-600 dark:text-green-400',
    bgColor: 'bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800',
  },
  {
    value: 'partial',
    icon: MinusCircleIcon,
    color: 'text-yellow-600 dark:text-yellow-400',
    bgColor: 'bg-yellow-50 dark:bg-yellow-950 border-yellow-200 dark:border-yellow-800',
  },
  {
    value: 'failure',
    icon: XCircleIcon,
    color: 'text-red-600 dark:text-red-400',
    bgColor: 'bg-red-50 dark:bg-red-950 border-red-200 dark:border-red-800',
  },
  {
    value: 'neutral',
    icon: AlertCircleIcon,
    color: 'text-gray-600 dark:text-gray-400',
    bgColor: 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700',
  },
]

// Training Suitability config
const TRAINING_SUITABILITY_CONFIG: {
  value: TrainingSuitability
  color: string
  bgColor: string
}[] = [
  {
    value: 'keep',
    color: 'text-green-600 dark:text-green-400',
    bgColor: 'bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800',
  },
  {
    value: 'review',
    color: 'text-yellow-600 dark:text-yellow-400',
    bgColor: 'bg-yellow-50 dark:bg-yellow-950 border-yellow-200 dark:border-yellow-800',
  },
  {
    value: 'discard',
    color: 'text-red-600 dark:text-red-400',
    bgColor: 'bg-red-50 dark:bg-red-950 border-red-200 dark:border-red-800',
  },
]

const NOTES_MAX_LENGTH = 500

export function ConversationQALabel({ conversation, className }: ConversationQALabelProps) {
  const { t } = useTranslation()
  const updateConversation = useUpdateConversation()
  const autoQAConversation = useAutoQAConversation()
  const { data: violationCodes = [] } = useViolationCodes()
  const user = useAuthStore((state) => state.user)
  const [isEditing, setIsEditing] = useState(false)

  const handleAutoQA = async () => {
    try {
      await autoQAConversation.mutateAsync({ conversationId: conversation.id })
      toast.success(t('resources.conversations.qa_label.auto_qa_success'))
    } catch (error) {
      console.error('Auto QA failed:', error)
      toast.error(getErrorMessage(error))
    }
  }

  // Parse existing QA label from meta
  const existingLabel = useMemo((): QALabel | null => {
    if (!conversation.meta) return null
    try {
      const meta = JSON.parse(conversation.meta)
      return meta.qa_label || null
    } catch {
      return null
    }
  }, [conversation.meta])

  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { isSubmitting },
  } = useForm<QALabelFormData>({
    defaultValues: {
      business_outcome: existingLabel?.business_outcome || '',
      outcome_notes: existingLabel?.outcome_notes || '',
      training_suitability: existingLabel?.training_suitability || '',
      adherence_score: existingLabel?.adherence_score || 7,
      violations: existingLabel?.violations || [],
    },
  })

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'violations',
  })

  // Reset form when conversation changes
  useEffect(() => {
    reset({
      business_outcome: existingLabel?.business_outcome || '',
      outcome_notes: existingLabel?.outcome_notes || '',
      training_suitability: existingLabel?.training_suitability || '',
      adherence_score: existingLabel?.adherence_score || 7,
      violations: existingLabel?.violations || [],
    })
    setIsEditing(!existingLabel)
  }, [conversation.id, existingLabel, reset])

  const businessOutcome = watch('business_outcome')
  const trainingSuitability = watch('training_suitability')
  const adherenceScore = watch('adherence_score')
  const outcomeNotes = watch('outcome_notes')

  const onSubmit = async (data: QALabelFormData) => {
    if (!data.business_outcome) {
      toast.error(t('resources.conversations.qa_label.business_outcome_required'))
      return
    }

    if (!data.training_suitability) {
      toast.error(t('resources.conversations.qa_label.training_suitability_required'))
      return
    }

    try {
      // Parse existing meta or create new object
      let meta: Record<string, unknown> = {}
      if (conversation.meta) {
        try {
          meta = JSON.parse(conversation.meta)
        } catch {
          meta = {}
        }
      }

      // Build QA label object
      const qaLabel: QALabel = {
        version: 'v2',
        business_outcome: data.business_outcome as BusinessOutcome,
        outcome_notes: data.outcome_notes.trim(),
        training_suitability: data.training_suitability as TrainingSuitability,
        adherence_score: data.adherence_score,
        violations: data.violations.map((v) => ({
          code: v.code,
          severity: v.severity,
          description: v.description,
        })),
        labeled_at: new Date().toISOString(),
        labeled_by: user?.id || 'unknown',
        source: 'human',
      }

      // Update meta with new QA label
      meta.qa_label = qaLabel

      await updateConversation.mutateAsync({
        id: conversation.id,
        data: { meta: JSON.stringify(meta) },
      })

      toast.success(t('resources.conversations.qa_label.saved'))
      setIsEditing(false)
    } catch (error) {
      console.error('Error saving QA label:', error)
      toast.error(t('resources.conversations.qa_label.save_failed'))
    }
  }

  const handleClear = async () => {
    try {
      let meta: Record<string, unknown> = {}
      if (conversation.meta) {
        try {
          meta = JSON.parse(conversation.meta)
        } catch {
          meta = {}
        }
      }

      // Remove QA label
      delete meta.qa_label

      await updateConversation.mutateAsync({
        id: conversation.id,
        data: { meta: JSON.stringify(meta) },
      })

      reset({
        business_outcome: '',
        outcome_notes: '',
        training_suitability: '',
        adherence_score: 7,
        violations: [],
      })
      toast.success(t('resources.conversations.qa_label.cleared'))
      setIsEditing(true)
    } catch (error) {
      console.error('Error clearing QA label:', error)
      toast.error(t('resources.conversations.qa_label.clear_failed'))
    }
  }

  // Get style for business outcome display
  const getOutcomeStyle = (outcome: BusinessOutcome) => {
    return BUSINESS_OUTCOME_CONFIG.find((c) => c.value === outcome) || BUSINESS_OUTCOME_CONFIG[3]
  }

  // Get style for training suitability display
  const getSuitabilityStyle = (suitability: TrainingSuitability) => {
    return TRAINING_SUITABILITY_CONFIG.find((c) => c.value === suitability) || TRAINING_SUITABILITY_CONFIG[1]
  }

  // Display existing label in read-only mode
  if (existingLabel && !isEditing) {
    const outcomeStyle = getOutcomeStyle(existingLabel.business_outcome)
    const suitabilityStyle = getSuitabilityStyle(existingLabel.training_suitability)
    const OutcomeIcon = outcomeStyle.icon
    const isLLMLabel = existingLabel.source === 'llm'

    return (
      <div className={cn('flex flex-col gap-4', className)}>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          {t('resources.conversations.qa_label.title')}
        </h3>

        <div
          className={cn(
            'rounded-lg border p-4',
            isLLMLabel
              ? 'bg-purple-50 dark:bg-purple-950 border-purple-200 dark:border-purple-800'
              : 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700'
          )}
        >
          {/* LLM indicator badge */}
          {isLLMLabel && (
            <div className="flex items-center gap-1.5 mb-3 text-purple-600 dark:text-purple-400">
              <Stars01Icon className="h-4 w-4" />
              <span className="text-xs font-medium">
                {t('resources.conversations.qa_label.llm_generated')}
                {existingLabel.model && ` (${existingLabel.model})`}
              </span>
              {existingLabel.confidence !== undefined && (
                <span className="text-xs text-purple-500 dark:text-purple-400 ml-1">
                  · {Math.round(existingLabel.confidence * 100)}% {t('resources.conversations.qa_label.confidence')}
                </span>
              )}
            </div>
          )}

          {/* Business Outcome */}
          <div className="mb-4">
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
              {t('resources.conversations.qa_label.business_outcome.label')}
            </div>
            <div className={cn('flex items-center gap-2', outcomeStyle.color)}>
              <OutcomeIcon className="h-5 w-5" />
              <span className="font-medium">
                {t(`resources.conversations.qa_label.business_outcome.${existingLabel.business_outcome}`)}
              </span>
            </div>
            {existingLabel.outcome_notes && (
              <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                {existingLabel.outcome_notes}
              </p>
            )}
          </div>

          {/* Training Suitability */}
          <div className="mb-4">
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
              {t('resources.conversations.qa_label.training_suitability.label')}
            </div>
            <div className="flex items-center gap-3">
              <span className={cn('font-medium', suitabilityStyle.color)}>
                {t(`resources.conversations.qa_label.training_suitability.${existingLabel.training_suitability}`)}
              </span>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {t('resources.conversations.qa_label.adherence_score.label')}: {existingLabel.adherence_score}/10
              </span>
            </div>
          </div>

          {/* Violations */}
          {existingLabel.violations && existingLabel.violations.length > 0 && (
            <div className="mb-4">
              <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                {t('resources.conversations.qa_label.violations.label')} ({existingLabel.violations.length})
              </div>
              <div className="flex flex-col gap-2">
                {existingLabel.violations.map((violation, idx) => (
                  <div
                    key={idx}
                    className={cn(
                      'rounded-md px-3 py-2 text-sm',
                      violation.severity === 'major'
                        ? 'bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800'
                        : 'bg-yellow-50 dark:bg-yellow-950 border border-yellow-200 dark:border-yellow-800'
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className={cn(
                          'text-xs font-medium px-1.5 py-0.5 rounded',
                          violation.severity === 'major'
                            ? 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
                            : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300'
                        )}
                      >
                        {t(`resources.conversations.qa_label.violations.severity.${violation.severity}`)}
                      </span>
                      <span className="font-medium text-gray-900 dark:text-gray-100">
                        {t(`resources.conversations.qa_label.violations.codes.${violation.code}`, violation.code)}
                      </span>
                    </div>
                    {violation.description && (
                      <p className="text-gray-600 dark:text-gray-400 mt-1">{violation.description}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
            {isLLMLabel
              ? t('resources.conversations.qa_label.generated_at', {
                  date: new Date(existingLabel.labeled_at).toLocaleString(),
                })
              : t('resources.conversations.qa_label.labeled_at', {
                  date: new Date(existingLabel.labeled_at).toLocaleString(),
                })}
          </div>
        </div>

        <div className="flex gap-2">
          <Button color="secondary" size="sm" onClick={() => setIsEditing(true)}>
            {isLLMLabel ? t('resources.conversations.qa_label.override') : t('resources.generic.edit')}
          </Button>
          <Button
            color="secondary"
            size="sm"
            onClick={handleClear}
            className="text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950"
          >
            {t('resources.generic.clear')}
          </Button>
        </div>
      </div>
    )
  }

  // Editing mode / New label form
  return (
    <div className={cn('flex flex-col gap-5', className)}>
      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
        {t('resources.conversations.qa_label.title')}
      </h3>

      {/* Auto QA button - only show when no label exists */}
      {!existingLabel && (
        <div className="flex flex-col gap-3 pb-3 border-b border-gray-200 dark:border-gray-700">
          <Button
            color="secondary"
            iconLeading={Stars01Icon}
            onClick={handleAutoQA}
            isLoading={autoQAConversation.isPending}
            className="w-full justify-center"
          >
            {autoQAConversation.isPending
              ? t('resources.conversations.qa_label.auto_qa_running')
              : t('resources.conversations.qa_label.auto_qa')}
          </Button>
          <div className="text-xs text-gray-500 dark:text-gray-400 text-center">
            {t('resources.conversations.qa_label.auto_qa_hint')}
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5">
        {/* Business Outcome */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
            {t('resources.conversations.qa_label.business_outcome.label')}
            <span className="text-error-500 ml-0.5">*</span>
          </label>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
            {t('resources.conversations.qa_label.business_outcome.hint')}
          </p>
          <Controller
            name="business_outcome"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <div className="grid grid-cols-2 gap-2">
                {BUSINESS_OUTCOME_CONFIG.map((config) => {
                  const isSelected = field.value === config.value
                  return (
                    <Button
                      key={config.value}
                      type="button"
                      color="secondary"
                      size="sm"
                      iconLeading={config.icon}
                      onClick={() => field.onChange(config.value)}
                      className={cn(
                        'justify-start',
                        isSelected && cn(config.bgColor, 'ring-2 ring-offset-1', config.color)
                      )}
                    >
                      {t(`resources.conversations.qa_label.business_outcome.${config.value}`)}
                    </Button>
                  )
                })}
              </div>
            )}
          />
        </div>

        {/* Outcome Notes */}
        {businessOutcome && (
          <Controller
            name="outcome_notes"
            control={control}
            render={({ field }) => (
              <div className="flex flex-col gap-1">
                <TextArea
                  label={t('resources.conversations.qa_label.outcome_notes.label')}
                  placeholder={t('resources.conversations.qa_label.outcome_notes.placeholder')}
                  value={field.value}
                  onChange={field.onChange}
                  rows={2}
                />
                <div className="text-xs text-gray-500 dark:text-gray-400 text-right">
                  {outcomeNotes.length}/{NOTES_MAX_LENGTH}
                </div>
              </div>
            )}
          />
        )}

        {/* Training Suitability */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
            {t('resources.conversations.qa_label.training_suitability.label')}
            <span className="text-error-500 ml-0.5">*</span>
          </label>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
            {t('resources.conversations.qa_label.training_suitability.hint')}
          </p>
          <Controller
            name="training_suitability"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <div className="flex gap-2">
                {TRAINING_SUITABILITY_CONFIG.map((config) => {
                  const isSelected = field.value === config.value
                  return (
                    <Button
                      key={config.value}
                      type="button"
                      color="secondary"
                      size="sm"
                      onClick={() => field.onChange(config.value)}
                      className={cn(
                        'flex-1 justify-center',
                        isSelected && cn(config.bgColor, 'ring-2 ring-offset-1', config.color)
                      )}
                    >
                      {t(`resources.conversations.qa_label.training_suitability.${config.value}`)}
                    </Button>
                  )
                })}
              </div>
            )}
          />
        </div>

        {/* Adherence Score */}
        {trainingSuitability && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
              {t('resources.conversations.qa_label.adherence_score.label')}: {adherenceScore}/10
            </label>
            <Controller
              name="adherence_score"
              control={control}
              render={({ field }) => (
                <input
                  type="range"
                  min="1"
                  max="10"
                  value={field.value}
                  onChange={(e) => field.onChange(parseInt(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
                />
              )}
            />
            <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
              <span>1 - {t('resources.conversations.qa_label.adherence_score.poor')}</span>
              <span>10 - {t('resources.conversations.qa_label.adherence_score.perfect')}</span>
            </div>
          </div>
        )}

        {/* Violations */}
        {trainingSuitability && trainingSuitability !== 'keep' && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                {t('resources.conversations.qa_label.violations.label')} ({fields.length})
              </label>
              <Button
                type="button"
                color="secondary"
                size="sm"
                iconLeading={PlusIcon}
                onClick={() => append({ code: 'other', severity: 'minor', description: '' })}
              >
                {t('resources.conversations.qa_label.violations.add')}
              </Button>
            </div>

            {fields.length > 0 && (
              <div className="flex flex-col gap-3">
                {fields.map((field, index) => (
                  <div
                    key={field.id}
                    className="rounded-md border border-gray-200 dark:border-gray-700 p-3 bg-gray-50 dark:bg-gray-800"
                  >
                    <div className="flex gap-2 mb-2">
                      <Controller
                        name={`violations.${index}.code`}
                        control={control}
                        render={({ field: codeField }) => (
                          <select
                            value={codeField.value}
                            onChange={codeField.onChange}
                            className="flex-1 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                          >
                            {violationCodes.map((vc) => (
                              <option key={vc.code} value={vc.code}>
                                {t(`resources.conversations.qa_label.violations.codes.${vc.code}`, vc.code)}
                              </option>
                            ))}
                          </select>
                        )}
                      />
                      <Controller
                        name={`violations.${index}.severity`}
                        control={control}
                        render={({ field: severityField }) => (
                          <select
                            value={severityField.value}
                            onChange={severityField.onChange}
                            className="w-28 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                          >
                            <option value="minor">
                              {t('resources.conversations.qa_label.violations.severity.minor')}
                            </option>
                            <option value="major">
                              {t('resources.conversations.qa_label.violations.severity.major')}
                            </option>
                          </select>
                        )}
                      />
                      <Button
                        type="button"
                        color="secondary"
                        size="sm"
                        iconLeading={Trash01Icon}
                        onClick={() => remove(index)}
                        className="text-red-600 hover:bg-red-50 dark:text-red-400"
                      />
                    </div>
                    <Controller
                      name={`violations.${index}.description`}
                      control={control}
                      render={({ field: descField }) => (
                        <TextArea
                          placeholder={t('resources.conversations.qa_label.violations.description_placeholder')}
                          value={descField.value}
                          onChange={descField.onChange}
                          rows={2}
                        />
                      )}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-2">
          <Button
            type="submit"
            color="primary"
            size="sm"
            isDisabled={isSubmitting || !businessOutcome || !trainingSuitability}
          >
            {isSubmitting ? t('resources.generic.saving') : t('resources.conversations.qa_label.save')}
          </Button>
          {existingLabel && (
            <Button
              type="button"
              color="secondary"
              size="sm"
              onClick={() => {
                reset({
                  business_outcome: existingLabel.business_outcome,
                  outcome_notes: existingLabel.outcome_notes,
                  training_suitability: existingLabel.training_suitability,
                  adherence_score: existingLabel.adherence_score,
                  violations: existingLabel.violations || [],
                })
                setIsEditing(false)
              }}
            >
              {t('resources.generic.cancel')}
            </Button>
          )}
        </div>
      </form>
    </div>
  )
}

export default ConversationQALabel
